import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as api from "constants/api";
import { SG_DELETE_DEBRIEF_PDF_REPORT } from "constants/actions";

// shadcn/ui components (consistent with PdfReport/List.js)
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Badge } from "components/ui/badge";
import { Separator } from "components/ui/separator";

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function toInt(value, fallback = null) {
  if (value === "" || value === null || value === undefined) return fallback;
  if (typeof value === "string" && value.trim() === "") return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? Math.trunc(n) : fallback;
}

function getRetryAfterMs(e) {
  const raw = e?.response?.headers?.["retry-after"];
  if (raw == null) return null;
  // Most commonly seconds. If it's a date, we ignore for now.
  const seconds = Number(raw);
  if (Number.isFinite(seconds) && seconds >= 0) return Math.round(seconds * 1000);
  return null;
}

function safeGetParticipation(detail) {
  const generated = detail?.additional_info?.generated_data;
  const participation =
    generated?.participation ||
    detail?.additional_info?.participation ||
    detail?.participation ||
    null;

  const responded =
    toInt(participation?.responded, null) ??
    toInt(participation?.completed, null) ??
    toInt(participation?.responses, null) ??
    null;

  const sent = toInt(participation?.sent, null) ?? null;
  const rate = typeof participation?.rate === "number" ? participation.rate : null;

  return { responded, sent, rate, raw: participation };
}

export default function Scanner() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [orgId, setOrgId] = useState("404");
  const [statusId, setStatusId] = useState("21"); // default: Completed
  const [threshold, setThreshold] = useState("5");
  const [delayMs, setDelayMs] = useState("1000");
  const [startPage, setStartPage] = useState("1");
  const [maxReports, setMaxReports] = useState(""); // optional safety limit
  const [showAllLogged, setShowAllLogged] = useState(false);

  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const [currentPage, setCurrentPage] = useState(null);
  const [totalPages, setTotalPages] = useState(null);
  const [processedCount, setProcessedCount] = useState(0);
  const [scanned, setScanned] = useState([]); // { id, organization, organization_name, status_text, report_name, updated_at, responded, sent, rate, flagged, error, deleted, deleting }
  const [errors, setErrors] = useState([]); // { id, status, message }

  const stopRef = useRef(false);
  const pauseRef = useRef(false);
  const runningRef = useRef(false);
  const delayRef = useRef(1000);
  const thresholdRef = useRef(5);
  const orgRef = useRef(0);
  const statusRef = useRef(21);
  const maxReportsRef = useRef(null);

  useEffect(() => {
    pauseRef.current = isPaused;
  }, [isPaused]);

  useEffect(() => {
    delayRef.current = Math.max(0, toInt(delayMs, 1000) ?? 1000);
  }, [delayMs]);

  useEffect(() => {
    thresholdRef.current = Math.max(0, toInt(threshold, 5) ?? 5);
  }, [threshold]);

  useEffect(() => {
    orgRef.current = Math.max(0, toInt(orgId, 0) ?? 0);
  }, [orgId]);

  useEffect(() => {
    // Allow blank to mean "no status filter"
    if (statusId === "" || (typeof statusId === "string" && statusId.trim() === "")) {
      statusRef.current = null;
      return;
    }
    statusRef.current = Math.max(0, toInt(statusId, 21) ?? 21);
  }, [statusId]);

  useEffect(() => {
    // Empty input means "no limit"
    if (maxReports === "" || (typeof maxReports === "string" && maxReports.trim() === "")) {
      maxReportsRef.current = null;
      return;
    }
    const n = toInt(maxReports, null);
    maxReportsRef.current = n == null ? null : Math.max(1, n);
  }, [maxReports]);

  const flagged = useMemo(() => scanned.filter((r) => r.flagged && !r.deleted), [scanned]);
  const flaggedOrError = useMemo(
    () => scanned.filter((r) => (r.flagged || r.error) && !r.deleted),
    [scanned]
  );
  const scannedCount = scanned.length;
  const flaggedCount = flagged.length;

  const waitIfPaused = useCallback(async () => {
    while (!stopRef.current && pauseRef.current) {
      await sleep(150);
    }
  }, []);

  const markBackoffOn429 = useCallback((id) => {
    // If we hit 429, increase delay (up to 10s) to be safe.
    const next = Math.min(Math.max(delayRef.current * 2, 1000), 10000);
    delayRef.current = next;
    setDelayMs(String(next));
    setErrors((prev) => [
      ...prev,
      { id, status: 429, message: `429 rate limit. Auto-backoff: delay is now ${next}ms.` },
    ]);
  }, []);

  const addScanRow = useCallback((row) => {
    setScanned((prev) => [...prev, row]);
  }, []);

  const startScan = useCallback(async () => {
    const org = orgRef.current;
    if (!org) {
      alert("Please enter a valid Organization ID.");
      return;
    }

    stopRef.current = false;
    pauseRef.current = false;
    runningRef.current = true;

    setIsRunning(true);
    setIsPaused(false);
    setCurrentPage(null);
    setTotalPages(null);
    setProcessedCount(0);
    setScanned([]);
    setErrors([]);

    const start = Math.max(1, toInt(startPage, 1) ?? 1);
    const hardLimit = maxReportsRef.current;

    let scannedSoFar = 0;
    let page = start;
    let computedTotalPages = null;

    try {
      while (!stopRef.current) {
        setCurrentPage(page);

        const listQueryParts = [`page=${page}`, `organization=${org}`];
        if (statusRef.current != null) listQueryParts.push(`status=${statusRef.current}`);
        const listQuery = listQueryParts.join("&");

        let list = null;
        // Protect list/page calls against 429s and other errors (avoid uncaught runtime errors)
        for (let attempt = 0; attempt < 5 && !stopRef.current; attempt += 1) {
          try {
            const listRes = await api.GET_DEBRIEF_PDF_REPORTS(listQuery);
            list = listRes?.data || {};
            break;
          } catch (e) {
            const status = e?.response?.status;
            const message = e?.response?.statusText || e?.message || "Unknown error";

            if (status === 429) {
              markBackoffOn429(`page:${page}`);
              await sleep(delayRef.current);
              continue;
            }

            setErrors((prev) => [...prev, { id: `page:${page}`, status, message }]);
            stopRef.current = true;
            break;
          }
        }

        if (!list || stopRef.current) break;
        const pageResults = Array.isArray(list?.results) ? list.results : [];

        if (!computedTotalPages && list?.count && list?.page_size) {
          computedTotalPages = Math.ceil(list.count / list.page_size);
          setTotalPages(computedTotalPages);
        }

        if (!pageResults.length) break;

        for (const r of pageResults) {
          if (stopRef.current) break;
          if (hardLimit && scannedSoFar >= hardLimit) break;

          await waitIfPaused();
          if (stopRef.current) break;

          const id = r?.id;
          const baseRow = {
            id,
            organization: r?.organization,
            organization_name: r?.organization_name,
            status_text: r?.status_text,
            report_name: r?.report_name,
            updated_at: r?.updated_at,
          };

          let detail = null;
          let lastErr = null;
          for (let attempt = 0; attempt < 5 && !stopRef.current; attempt += 1) {
            try {
              const detailRes = await api.FETCH_DEBRIEF_PDF_REPORT({ id });
              detail = detailRes?.data || {};
              lastErr = null;
              break;
            } catch (e) {
              lastErr = e;
              const status = e?.response?.status;
              if (status === 429) {
                markBackoffOn429(id);
                const retryAfterMs = getRetryAfterMs(e);
                await sleep(Math.max(delayRef.current, retryAfterMs ?? 0));
                continue;
              }
              break;
            }
          }

          try {
            if (!detail) {
              const status = lastErr?.response?.status;
              const message =
                lastErr?.response?.statusText || lastErr?.message || "Unknown error";
              if (status !== 429) setErrors((prev) => [...prev, { id, status, message }]);

              addScanRow({
                ...baseRow,
                responded: null,
                sent: null,
                rate: null,
                flagged: false,
                error: { status, message },
                deleted: false,
                deleting: false,
              });
              setProcessedCount((p) => p + 1);
              scannedSoFar += 1;
              await sleep(delayRef.current);
              continue;
            }

            const { responded, sent, rate, raw } = safeGetParticipation(detail);

            // If there's no participation key/data at all, don't log this report.
            if (!raw) {
              setProcessedCount((p) => p + 1);
              scannedSoFar += 1;
              await sleep(delayRef.current);
              continue;
            }

            const flaggedRow =
              responded != null && responded < thresholdRef.current;

            addScanRow({
              ...baseRow,
              responded,
              sent,
              rate,
              flagged: flaggedRow,
              error: null,
              deleted: false,
              deleting: false,
            });
            setProcessedCount((p) => p + 1);
          } catch (e) {
            const status = e?.response?.status;
            const message = e?.response?.statusText || e?.message || "Unknown error";

            if (status === 429) {
              markBackoffOn429(id);
            } else {
              setErrors((prev) => [...prev, { id, status, message }]);
            }

            addScanRow({
              ...baseRow,
              responded: null,
              sent: null,
              rate: null,
              flagged: false,
              error: { status, message },
              deleted: false,
              deleting: false,
            });
            setProcessedCount((p) => p + 1);
          }

          scannedSoFar += 1;
          await sleep(delayRef.current);
        }

        if (stopRef.current) break;
        if (hardLimit && scannedSoFar >= hardLimit) break;

        page += 1;
        if (computedTotalPages && page > computedTotalPages) break;
      }
    } finally {
      runningRef.current = false;
      setIsRunning(false);
      setIsPaused(false);
    }
  }, [addScanRow, markBackoffOn429, startPage, waitIfPaused]);

  const pause = useCallback(() => {
    if (!runningRef.current) return;
    setIsPaused(true);
  }, []);

  const resume = useCallback(() => {
    if (!runningRef.current) return;
    setIsPaused(false);
  }, []);

  const stop = useCallback(() => {
    stopRef.current = true;
    setIsPaused(false);
  }, []);

  const confirmAndDelete = useCallback(
    async (row) => {
      const id = row?.id;
      if (!id) return;

      const ok = window.confirm(
        `Delete report #${id}?\n\nOrg: #${row?.organization} - ${row?.organization_name || ""}\nResponded: ${
          row?.responded ?? "—"
        }\n\nThis cannot be undone.`
      );
      if (!ok) return;

      setScanned((prev) =>
        prev.map((r) => (r.id === id ? { ...r, deleting: true } : r))
      );

      dispatch({ type: SG_DELETE_DEBRIEF_PDF_REPORT, payload: { id } });

      // Optimistic UI update; if backend rejects, the ALL_ERRORS store will reflect it elsewhere.
      setTimeout(() => {
        setScanned((prev) =>
          prev.map((r) =>
            r.id === id ? { ...r, deleting: false, deleted: true } : r
          )
        );
      }, 600);
    },
    [dispatch]
  );

  const statusBadge = (row) => {
    if (row.deleted) return <Badge variant="secondary">Deleted</Badge>;
    if (row.error) return <Badge variant="destructive">Error</Badge>;
    if (row.flagged) return <Badge variant="destructive">Below threshold</Badge>;
    return <Badge variant="outline">OK</Badge>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 md:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">PDF Report Scanner</h1>
            <p className="text-sm text-slate-600 mt-1">
              Scan reports for an org, throttle detail calls, flag low participation, delete if needed.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate("/app/pdf-reports")}>
              Back to Reports
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Scan settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-4">
              <div className="space-y-2">
                <Label>Organization ID</Label>
                <Input
                  value={orgId}
                  onChange={(e) => setOrgId(e.target.value)}
                  inputMode="numeric"
                  placeholder="404"
                />
              </div>
              <div className="space-y-2">
                <Label>Status ID (default Completed=21)</Label>
                <Input
                  value={statusId}
                  onChange={(e) => setStatusId(e.target.value)}
                  inputMode="numeric"
                  placeholder="21"
                />
              </div>
              <div className="space-y-2">
                <Label>Participation threshold (responded)</Label>
                <Input
                  value={threshold}
                  onChange={(e) => setThreshold(e.target.value)}
                  inputMode="numeric"
                  placeholder="5"
                />
              </div>
              <div className="space-y-2">
                <Label>Delay between detail calls (ms)</Label>
                <Input
                  value={delayMs}
                  onChange={(e) => setDelayMs(e.target.value)}
                  inputMode="numeric"
                  placeholder="1000"
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-4">
              <div className="space-y-2">
                <Label>Start page</Label>
                <Input
                  value={startPage}
                  onChange={(e) => setStartPage(e.target.value)}
                  inputMode="numeric"
                  placeholder="1"
                />
              </div>
              <div className="space-y-2 md:col-span-1">
                <Label>Max reports (optional safety)</Label>
                <Input
                  value={maxReports}
                  onChange={(e) => setMaxReports(e.target.value)}
                  inputMode="numeric"
                  placeholder="e.g. 100"
                />
              </div>
              <div className="md:col-span-3 flex flex-wrap items-end gap-2">
                <Button onClick={startScan} disabled={isRunning}>
                  Start scan
                </Button>
                <Button variant="outline" onClick={pause} disabled={!isRunning || isPaused}>
                  Pause
                </Button>
                <Button variant="outline" onClick={resume} disabled={!isRunning || !isPaused}>
                  Resume
                </Button>
                <Button variant="destructive" onClick={stop} disabled={!isRunning}>
                  Stop
                </Button>
              </div>
            </div>

            <Separator />

            <div className="flex flex-wrap gap-2 items-center">
              <Badge variant="outline">Processed: {processedCount}</Badge>
              <Badge variant="outline">Logged: {scannedCount}</Badge>
              <Badge variant={flaggedCount > 0 ? "destructive" : "secondary"}>
                Flagged: {flaggedCount}
              </Badge>
              <Badge variant={errors.length > 0 ? "destructive" : "secondary"}>
                Errors: {errors.length}
              </Badge>
              <Badge variant="outline">
                Page: {currentPage ?? "—"} / {totalPages ?? "—"}
              </Badge>
              <Button
                variant="outline"
                onClick={() => setShowAllLogged((v) => !v)}
              >
                {showAllLogged ? "Hide all logged" : "Show all logged"}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Flagged (below threshold) + Errors</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {flaggedOrError.length === 0 ? (
              <div className="text-sm text-slate-600">No flagged reports or errors yet.</div>
            ) : (
              <div className="space-y-2">
                {flaggedOrError.map((row) => (
                  <div
                    key={row.id}
                    className="rounded-lg border bg-white p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-semibold">#{row.id}</div>
                        {statusBadge(row)}
                        <div className="text-sm text-slate-600">
                          Org #{row.organization} {row.organization_name ? `- ${row.organization_name}` : ""}
                        </div>
                      </div>
                      {row.error ? (
                        <div className="text-sm text-red-700">
                          {row.error.status ? `${row.error.status}: ` : ""}
                          {row.error.message}
                        </div>
                      ) : (
                        <div className="text-sm text-slate-700">
                          Responded: <span className="font-semibold">{row.responded ?? "—"}</span>{" "}
                          {row.sent != null ? (
                            <span className="text-slate-500">/ Sent: {row.sent}</span>
                          ) : null}
                          {row.rate != null ? (
                            <span className="text-slate-500"> / Rate: {Math.round(row.rate * 1000) / 10}%</span>
                          ) : null}
                        </div>
                      )}
                      {row.report_name ? (
                        <div className="text-xs text-slate-500">{row.report_name}</div>
                      ) : null}
                    </div>

                    <div className="flex gap-2 flex-wrap">
                      <Button
                        variant="outline"
                        onClick={() => navigate(`/app/pdf-reports/details/${row.id}`)}
                      >
                        Open details
                      </Button>
                      <Button
                        variant="destructive"
                        disabled={row.deleting || row.deleted}
                        onClick={() => confirmAndDelete(row)}
                      >
                        {row.deleting ? "Deleting..." : "Delete report"}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {showAllLogged && (
          <Card>
            <CardHeader>
              <CardTitle>All logged (has participation)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {scanned.length === 0 ? (
                <div className="text-sm text-slate-600">Nothing logged yet.</div>
              ) : (
                scanned
                  .slice()
                  .reverse()
                  .map((row) => (
                    <div
                      key={row.id}
                      className="rounded-md border bg-white px-3 py-2 flex items-center justify-between gap-3"
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-medium">#{row.id}</div>
                        {statusBadge(row)}
                        <div className="text-sm text-slate-600">
                          Org #{row.organization}
                        </div>
                        <div className="text-sm text-slate-600">
                          responded: {row.responded ?? "—"}
                        </div>
                        {row.error ? (
                          <div className="text-xs text-red-700">
                            {row.error.status ? `${row.error.status}: ` : ""}
                            {row.error.message}
                          </div>
                        ) : null}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/app/pdf-reports/details/${row.id}`)}
                        >
                          Details
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          disabled={row.deleting || row.deleted}
                          onClick={() => confirmAndDelete(row)}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}


